import { create } from 'zustand';
import { FormState, FormNode, FormTreeStore, FormToken, ok, err, Result, ZExtensionProtocol, FormTreeStoreInstance, isErr } from './types';
import { useRef } from 'react';
import { debounce } from 'lodash';
import { runOtherThread, waitMilis } from '../asyncUtils';



const enableDebug = false

const Debuger : typeof console = 
  import.meta.env.MODE === 'development' && enableDebug
  ? console
  : {
      log: () => {}
    } as any

/**
 * Create the Zustand store for form tree management
 */
export function createZTreeStore() {
  Debuger.log('>>>>>> create store')
  // make sure parent->child hierarchy build after the register
  const hierarchyBuildQueue : FormToken[] = []

  const _runOtherThread = debounce(runOtherThread, 50)

  const buildHierarchyAsync = async (get: () => FormTreeStore) => {
    const queue = [...hierarchyBuildQueue].sort((a, b) => a.path < b.path ? -1 : 1)
    const state = get()
    hierarchyBuildQueue.length = 0
    for (const token of queue) {
      if (state.nodeRegistry.has(token.parentId!)) {
        const pnode = state.nodeRegistry.get(token.parentId!)!
        pnode.children.set(getFieldNameFromPath(token.path), token)

        // no need to run set(), hierarchy establishment is silent
      } else {
        // requeue the token, since its parent node is not ready
        hierarchyBuildQueue.push(token)
      }
    }
    if (hierarchyBuildQueue.length > 0) {
      await waitMilis(50)
      buildHierarchyAsync(get)
    }
  }

  return create<FormTreeStore>((set, get) => ({
    // State
    formTree: null,
    nodeRegistry: new Map(),

    // Actions
    register: <S, P>(token: FormToken, initialState: FormState<S, P>) => {      
      set((state) => {
        // let stateIntermediate = state
        if (state.nodeRegistry.has(token.id)) { return state; }
        Debuger.log('>>>> register ..', token.id)

        // Build node tree structure
        const newNode: FormNode<S, P> = {
          id: token.id,
          token,
          state: initialState,
          children: new Map(),
        };

        // If this is the root node, set it as formTree
        const isRoot = !token.parentId;
        const newFormTree = isRoot ? newNode : state.formTree;
        if (!isRoot) {
          hierarchyBuildQueue.push(token)
        }

        state.nodeRegistry.set(token.id, newNode);

        _runOtherThread(() => {
          buildHierarchyAsync(get)
        })
        return {
          ...state,
          formTree: newFormTree,
        };
      });
    },

    unregister: (nodeId: string) => {
      Debuger.log('>>>> unregister..', nodeId)
      const i = hierarchyBuildQueue.findIndex(t => t.id === nodeId)
      if (i > -1) {
        hierarchyBuildQueue.splice(i, 1)
      }
      set((state) => {
        const node = state.nodeRegistry.get(nodeId);
        if (!node) return state;

        // Remove from parent's children
        const parentNode = node.token.parentId && state.nodeRegistry.get(node.token.parentId)
        if (parentNode) {
          const fieldName = getFieldNameFromPath(node.token.path);
          parentNode.children.delete(fieldName);
        }

        // Remove from registry
        state.nodeRegistry.delete(nodeId);

        // Update formTree if this was the root
        const newFormTree = state.formTree?.id === nodeId ? null : state.formTree;

        return {
          ...state,
          formTree: newFormTree,
        };
      });
    },

    updateNode: <S, P = S>(
      nodeId: string, 
      updateOrchestrator: (
        nodeSet: (
          newState: | FormState<S, P>
                    | ((v0: FormState<S, P>)=>FormState<S, P>)
        ) => void,
        nodeGet: () => FormState<S, P>
      ) => void | Promise<void>
    ) => {
      const nodeSet = (
        newState: | FormState<S, P>
                  | ((v0: FormState<S, P>)=>FormState<S, P>)
      ) => {
        set((state) => {
          const node = state.nodeRegistry.get(nodeId);
          if (!node) return state;
          let state_1 : FormState<S, P> = null as any
          if (newState instanceof Function) {
            state_1 = newState(node.state)
          } else {
            state_1 = newState
          }
          if (node.state === state_1) { return state; }
  
          const newNode = { ...node, state: state_1 };
  
          // Update registry
          state.nodeRegistry.set(nodeId, newNode);
  
          // no need to update parent..
  
          return {
            ...state,
            formTree: state.formTree?.id === nodeId ? newNode : state.formTree,
          };
        });
      }
      const nodeGet = () => {
        return get().nodeRegistry.get(nodeId)?.state as FormState<S, P>
      }
      updateOrchestrator(nodeSet, nodeGet)
    },
    
    installNodeProtocolMetadata: (nodeId: string, protocol: ZExtensionProtocol, metadata: any) => {
      set((state) => {
        const node = state.nodeRegistry.get(nodeId)
        if (!node) { return state }
        
        Debuger.log('> install meta..', nodeId, protocol.metadataKey)
        const newNode = { ...node, 
          state: {...node.state,
            metadata: {...node.state.metadata,
              [protocol.metadataKey]: metadata,
            }
          }
        };

        // Update registry
        state.nodeRegistry.set(nodeId, newNode);

        return {
          ...state,
        };
      })
    },

    invokeNodeProtocol: async (nodeId: string, protocol: ZExtensionProtocol) : Promise<void> => {
      const node = get().nodeRegistry.get(nodeId)
      if (!node) { return }

      // the protocol execution itself may mutate states, 
      // so we have to arrange for a safe to access all the arguments
      const safeInvokeNode = async (node : FormNode) => {
        const metadata = node.state.metadata && node.state.metadata[protocol.metadataKey]
        const safeChildrenCollection = new Map([...node.children.entries()].map(([k, t]) => [k, get().nodeRegistry.get(t.id)!]))
        const currNodeId = node.id
        const getNode = () => get().nodeRegistry.get(currNodeId)!
        return await protocol.perform(getNode, metadata, safeChildrenCollection, safeInvokeNode)
      }

      let finalResult : Result<any, any> = null as any
      try {
        const result = await safeInvokeNode(node)
        if (isErr(result)) {
          finalResult = result
        } else {
          finalResult = ok(await safeInvokeNode(node))
        }
      } catch (error) {
        finalResult = protocol.protocolDefault ? ok(protocol.protocolDefault) : err(error)
      }
      if (protocol.postProcess) {
        protocol.postProcess(finalResult)
      }
    },

    getNode: (nodeId: string) => {
      return get().nodeRegistry.get(nodeId);
    },

    getDescendants: (nodeId: string) => {
      const node = get().nodeRegistry.get(nodeId);
      if (!node) return [];

      const descendants: FormNode[] = [];
      const fromChildrnTokens = (ctokens: FormToken[]) => ctokens.map(t => get().nodeRegistry.get(t.id)!)
      const stack = fromChildrnTokens([...node.children.values()]);

      while (stack.length > 0) {
        const current = stack.pop()!;
        descendants.push(current);
        stack.push(...fromChildrnTokens([...current.children.values()]));
      }

      return descendants;
    },

    traverseDescendants: (nodeId: string, traverse: (next: () => FormNode|null) => void) => {
      const node = get().nodeRegistry.get(nodeId);
      if (!node) return;
      const fromChildrnTokens = (ctokens: FormToken[]) => ctokens.map(t => get().nodeRegistry.get(t.id)!)

      function* descendantsGenerator() {
        const stack = fromChildrnTokens([...node!.children.values()]);
        
        while (stack.length > 0) {
          const current = stack.pop()!;
          yield current;
          stack.push(...fromChildrnTokens([...current.children.values()]));
        }
      }
      const gen = descendantsGenerator()
      const next = () => {
        const { done, value } = gen.next()
        return done ? null : value
      }
      traverse(next)
    },

    getAncestors: (nodeId: string) => {
      const nodeRegistry = get().nodeRegistry
      const node = nodeRegistry.get(nodeId);
      if (!node) return [];

      const ancestors: FormNode[] = [];
      let current = node.token.parentId && nodeRegistry.get(node.token.parentId);

      while (current) {
        ancestors.push(current);
        current = current.token.parentId && nodeRegistry.get(current.token.parentId);
      }

      return ancestors;
    },

    resetStore: () => {
      set((state) => ({
        ...state,
        formTree: null,
        nodeRegistry: new Map(),
      }))
    },
  }));
}


export const useCreateZTreeStore = () => {
  const doneRef = useRef(null as FormTreeStoreInstance|null)
  // use useRef instead of useMemo: to fight React Strict Mode...
  if (doneRef.current) { return doneRef.current }
  doneRef.current = createZTreeStore()
  return doneRef.current
}

/**
 * Extract field name from a lens path
 */
function getFieldNameFromPath(path: string): string {
  const lastDot = path.lastIndexOf('.');
  return lastDot >= 0 ? path.substring(lastDot + 1) : path;
}

/**
 * Default store instance
 */
export const defaultFormStore = createZTreeStore();

